import React, { useState, useEffect } from 'react';
import { Loader, CheckCircle, XCircle, UserX, AlertTriangle } from 'lucide-react';
import { resignationAPI, employeeAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useActiveEstId } from './AuthContext';
import { getLocalToday } from '../utils/dateUtils';
import DateInput from './DateInput';
import './ApplyLeave.css';

const EMPTY = {
  employee_id: '',
  last_working_day: getLocalToday(),
  reason: '',
  disable_login: true,
};

export default function TerminateEmployee() {
  const activeEstId = useActiveEstId?.();
  const [establishments, setEstablishments] = useState([]);
  const [estId, setEstId] = useState(activeEstId || '');
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const res = await establishmentAPI.getAll();
        setEstablishments(res.establishments || []);
        if (!estId && res.establishments?.length === 1) setEstId(res.establishments[0].id);
      } catch (err) {
        setToast({ type: 'error', msg: err.message || 'Could not load organizations.' });
      }
    })();
  }, []);

  useEffect(() => {
    if (!estId) { setEmployees([]); return; }
    setLoadingEmps(true);
    (async () => {
      try {
        const res = await employeeAPI.getByEstablishment(estId);
        setEmployees(res.employees || []);
      } catch (err) {
        setToast({ type: 'error', msg: err.message || 'Could not load employees.' });
      } finally {
        setLoadingEmps(false);
      }
    })();
  }, [estId]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const selectedEmp = employees.find(e => e.employee_id === form.employee_id);
  const empLabel = selectedEmp
    ? `${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''} (${selectedEmp.employee_id})`.trim()
    : '';

  const submit = async () => {
    if (!form.employee_id) {
      setToast({ type: 'error', msg: 'Please select an employee.' });
      return;
    }
    if (!form.last_working_day) {
      setToast({ type: 'error', msg: 'Last working day is required.' });
      return;
    }
    if (!window.confirm(
      `Terminate ${empLabel}?\nLast working day: ${form.last_working_day}\nDisable login: ${form.disable_login ? 'Yes' : 'No'}\n\nThis is immediate and bypasses the approval flow.`,
    )) return;

    setSubmitting(true);
    try {
      await resignationAPI.adminTerminate({
        employeeId: form.employee_id,
        lastWorkingDay: form.last_working_day,
        reason: form.reason,
        disableLogin: form.disable_login,
      });
      notifySavedAndReload(`${empLabel} marked as exited.`);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to terminate employee.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {toast && (
        <div className={`al-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="al-card">
        <p className="al-card-label">
          <UserX size={14} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
          Terminate / Mark as Resigned
        </p>

        <div className="al-form-grid">
          <div className="al-field">
            <label className="al-label">Organization <span>*</span></label>
            <select value={estId} onChange={e => { setEstId(e.target.value); setForm(EMPTY); }} className="al-input">
              <option value="">— Select —</option>
              {establishments.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>

          <div className="al-field">
            <label className="al-label">Employee <span>*</span></label>
            <select value={form.employee_id} onChange={e => set('employee_id', e.target.value)}
              className="al-input" disabled={!estId || loadingEmps}>
              <option value="">{loadingEmps ? 'Loading…' : '— Select —'}</option>
              {employees.map(e => (
                <option key={e.employee_id} value={e.employee_id}>
                  {`${e.first_name || ''} ${e.last_name || ''}`.trim()} ({e.employee_id})
                  {e.employment_status === 'exited' ? ' — already exited' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="al-field">
            <label className="al-label">Last Working Day <span>*</span></label>
            <DateInput value={form.last_working_day} onChange={e => set('last_working_day', e.target.value)} className="al-input" />
          </div>

          <div className="al-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={form.disable_login}
                onChange={e => set('disable_login', e.target.checked)} />
              Disable login immediately
            </label>
          </div>

          <div className="al-field" style={{ gridColumn: '1 / -1' }}>
            <label className="al-label">Reason</label>
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)}
              className="al-input" rows={3}
              placeholder="Internal note (e.g., performance, restructure, mutual agreement)…" />
          </div>
        </div>

        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fee2e2', borderRadius: '6px', fontSize: '0.8rem', color: '#991b1b', display: 'flex', gap: '0.5rem' }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>This bypasses the employee submission/approval flow. Use only for involuntary exits or when finalizing an already-agreed departure.</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button onClick={submit} disabled={submitting || !form.employee_id}
            className="mp-btn-save" style={{ background: '#dc2626' }}>
            {submitting ? <Loader size={15} className="mp-spinner-sm" /> : <UserX size={15} />}
            {submitting ? 'Terminating…' : 'Terminate Employee'}
          </button>
        </div>
      </div>
    </>
  );
}
